import { authFetch } from "@/lib/auth-fetch";
import { apiUrl } from "@/lib/api-base";
import { saveCalendarCodeVerifier } from "@/lib/calendar-oauth-complete";
import type { CalendarConnection, Rhythm, StepSchedule, TimeRange } from "@/lib/calendar";

/**
 * The calendar API, `/api/calendar/*`. The backend holds the Google tokens and does every
 * Google call; this client never talks to Google directly.
 *
 *   GET    /calendar/connection                         → { connection | null }   (404: feature not deployed)
 *   POST   /calendar/google/start   { returnTo, freeBusy } → { url }   (Google's screen, same tab)
 *   PATCH  /calendar/connection     { defaultCalendarId?, suggestFreeTimes? } → { connection }
 *   DELETE /calendar/connection                         → 204   (events already added stay)
 *   GET    /calendar/busy?calendarId&from&to             → { busy: TimeRange[] }   (ranges only, never names)
 *   GET    /calendar/schedules                          → { schedules }
 *   POST   /calendar/schedules      CreateScheduleBody   → { schedule }
 *   PATCH  /calendar/schedules/:actionId/events/:eventId { start, end } → { schedule }
 *   PUT    /calendar/schedules/:actionId { calendarId, rhythm, events } → { schedule }   (replaces future dates)
 *   DELETE /calendar/schedules/:actionId?scope=all|future → { schedule | null }
 *
 * Errors carry `{ error: CalendarErrorCode }`. After Google, the backend redirects to
 * `returnTo` with `?calendar=connected|missing_events|cancelled`.
 */

export type CalendarErrorCode = "connection_ended" | "free_busy_not_allowed" | "unavailable" | "unknown";

export class CalendarApiError extends Error {
  constructor(
    readonly code: CalendarErrorCode,
    readonly status: number,
  ) {
    super(`Calendar API ${status}: ${code}`);
    this.name = "CalendarApiError";
  }
}

export function calendarErrorCode(err: unknown): CalendarErrorCode | null {
  return err instanceof CalendarApiError ? err.code : null;
}

async function call<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await authFetch(apiUrl(`/calendar${path}`), {
    ...init,
    headers: { ...(init.body ? { "Content-Type": "application/json" } : {}), ...init.headers },
  });
  if (res.ok) return (res.status === 204 ? undefined : await res.json()) as T;

  let code: CalendarErrorCode = res.status === 404 ? "unavailable" : "unknown";
  try {
    const body = (await res.json()) as { error?: string };
    if (body.error === "connection_ended" || body.error === "free_busy_not_allowed") code = body.error;
  } catch {
    // Not JSON — keep the status-based code.
  }
  throw new CalendarApiError(code, res.status);
}

const json = (body: unknown): RequestInit => ({ body: JSON.stringify(body) });

export async function getConnection(): Promise<CalendarConnection | null> {
  const data = await call<{ connection: CalendarConnection | null }>("/connection");
  return data.connection ?? null;
}

/** Where to send the person for Google's screen. `freeBusy` asks for the optional permission. */
export async function startGoogleConnect(returnTo: string, freeBusy = true): Promise<string> {
  const data = await call<{ url: string; codeVerifier: string }>("/google/start", {
    method: "POST",
    ...json({ returnTo, freeBusy }),
  });
  // Held only by this browser (sessionStorage) for the one request that completes the
  // connection when Google redirects back — never sent anywhere except that request.
  saveCalendarCodeVerifier(data.codeVerifier);
  return data.url;
}

export async function updateConnection(
  patch: Partial<Pick<CalendarConnection, "defaultCalendarId" | "suggestFreeTimes">>,
): Promise<CalendarConnection> {
  const data = await call<{ connection: CalendarConnection }>("/connection", { method: "PATCH", ...json(patch) });
  return data.connection;
}

export async function disconnect(): Promise<void> {
  await call<void>("/connection", { method: "DELETE" });
}

export async function getBusy(calendarId: string, from: Date, to: Date): Promise<TimeRange[]> {
  const q = new URLSearchParams({ calendarId, from: from.toISOString(), to: to.toISOString() });
  const data = await call<{ busy: TimeRange[] }>(`/busy?${q}`);
  return data.busy ?? [];
}

export async function listSchedules(): Promise<StepSchedule[]> {
  const data = await call<{ schedules: StepSchedule[] }>("/schedules");
  return data.schedules ?? [];
}

export interface CreateScheduleBody {
  actionId: string;
  journalId: string;
  calendarId: string;
  /** The step, word for word — it becomes the event's title. */
  title: string;
  kind: "once" | "rhythm";
  events: TimeRange[];
  rhythm: Rhythm | null;
}

export async function createSchedule(body: CreateScheduleBody): Promise<StepSchedule> {
  const data = await call<{ schedule: StepSchedule }>("/schedules", { method: "POST", ...json(body) });
  return data.schedule;
}

export async function moveEvent(actionId: string, eventId: string, range: TimeRange): Promise<StepSchedule> {
  const data = await call<{ schedule: StepSchedule }>(`/schedules/${actionId}/events/${eventId}`, {
    method: "PATCH",
    ...json(range),
  });
  return data.schedule;
}

export async function replaceRhythm(
  actionId: string,
  body: { calendarId: string; rhythm: Rhythm; events: TimeRange[] },
): Promise<StepSchedule> {
  const data = await call<{ schedule: StepSchedule }>(`/schedules/${actionId}`, { method: "PUT", ...json(body) });
  return data.schedule;
}

/** `future` keeps events that already happened as a record; `all` removes every one. */
export async function removeSchedule(actionId: string, scope: "all" | "future"): Promise<StepSchedule | null> {
  const data = await call<{ schedule: StepSchedule | null }>(`/schedules/${actionId}?scope=${scope}`, {
    method: "DELETE",
  });
  return data.schedule ?? null;
}
